import * as XLSX from 'xlsx';
import AccountImportStore, { accountImportSchema } from '../store/AccountImportStore';
import { Account } from '../../../../../stores/AccountStore';
import { SheetError } from '../../../../../features/data-management/leads/ExcelImportStore';
import { OptionsStore } from '../../../../../features/common/OptionsLoader';
import { isEmpty } from '../../../../utils/isEmpty';

const useAccountImport = () => {
    const open = AccountImportStore.useState((s) => s.open);
    const workbook = AccountImportStore.useState((s) => s.workbook);
    const worksheet = AccountImportStore.useState((s) => s.worksheet);
    const revalidate = AccountImportStore.useState((s) => s.revalidate);
    const loaded = AccountImportStore.useState((s) => s.loaded);
    const errors = AccountImportStore.useState((s) => s.errors);
    const accounts = AccountImportStore.useState((s) => s.accounts);
    const unmappedColumns = AccountImportStore.useState((s) => s.unmappedColumns);
    const mapSchema = AccountImportStore.useState((s) => s.mapSchema);
    const allColumns = AccountImportStore.useState((s) => s.allColumns);
    const fileError = AccountImportStore.useState((s) => s.fileError);
    const hasUnmappedRequired = AccountImportStore.useState((s) => s.hasUnmappedRequired);
    const currentStep = AccountImportStore.useState((s) => s.currentStep);
    const industries = OptionsStore.useState((s) => s.industries);
    const countries = OptionsStore.useState((s) => s.countries);
    const regions = OptionsStore.useState((s) => s.regions);

    const onClose = () => {
        AccountImportStore.update((s) => {
            s.open = false;
            s.currentStep = 1;
            s.workbook = null;
            s.worksheet = null;
            s.mapSchema = [];
            s.loaded = false;
            s.fileError = false;
            s.hasUnmappedRequired = false;
            s.allColumns = [];
            s.accounts = [];
            s.errors = [];
            s.unmappedColumns = [];
        });
    };

    const openAccountImport = () => {
        AccountImportStore.update((s) => {
            s.open = true;
        });
    };

    const onFileUpload = (files: FileList) => {
        const reader = new FileReader();

        if (files && files.length !== 0) {
            reader.onload = (e) => {
                if (e.target) {
                    const data = e.target.result;
                    const tempWorkbook = XLSX.read(data, { type: 'array' });
                    const tempWorksheet = tempWorkbook.Sheets[tempWorkbook.SheetNames[0]];

                    const exceljsonObj = XLSX.utils.sheet_to_json(tempWorksheet, { defval: '' }) as object[];

                    const { mapSchema, unmappedColumns, tempErrors, sheetColumnNames, fileError, hasUnmappedRequired } =
                        validateMappings(exceljsonObj);

                    AccountImportStore.update((s) => {
                        s.workbook = tempWorkbook;
                        s.worksheet = tempWorksheet;
                        s.errors = tempErrors;
                        s.unmappedColumns = unmappedColumns;
                        s.fileError = fileError;
                        s.allColumns = sheetColumnNames;
                        s.mapSchema = mapSchema;
                        s.hasUnmappedRequired = hasUnmappedRequired;
                        s.loaded = tempErrors.filter((s) => s.errorType === 'error').length === 0;
                    });
                } else {
                    alert('error');
                }
            };

            reader.readAsArrayBuffer(files[0]);
        } else {
            alert('error');
        }
    };

    const validateMappings = (sheet: object[]) => {
        let unmappedColumns = [] as string[];
        const mapSchema = structuredClone([...accountImportSchema]);
        const tempErrors = [] as SheetError[];
        let hasUnmappedRequired = false;

        if (sheet.length === 0) {
            tempErrors.push({
                rowNum: 1,
                colNum: 1,
                message: 'file contains no records',
                errorType: 'error',
            });

            return {
                unmappedColumns,
                tempErrors,
                sheetColumnNames: [],
                fileError: true,
                mapSchema: [],
                hasUnmappedRequired,
            };
        }

        const sheetColumnNames = Object.keys(sheet[0] as object);
        let fileError = false;

        for (let columnNumber = 0; columnNumber < sheetColumnNames.length; columnNumber++) {
            const columnName = sheetColumnNames[columnNumber];

            if (columnName === '__EMPTY') {
                tempErrors.push({
                    rowNum: 1,
                    colNum: columnNumber + 1,
                    message: `Column ${columnNumber} has no heading.  Please add the heading in your sheet and upload the file again.`,
                    errorType: 'error',
                });

                fileError = true;
            } else if (containsWhitespace(columnName)) {
                tempErrors.push({
                    rowNum: 1,
                    colNum: columnNumber + 1,
                    message: `Column ${
                        columnNumber + 1
                    } has a heading that contains spaces.  Please remove the spaces from the heading in your sheet and upload the file again.`,
                    errorType: 'error',
                });

                fileError = true;
            } else {
                const indexItem = mapSchema.find(
                    (s) => s.columnName.toLowerCase().replace(' ', '') === sheetColumnNames[columnNumber].toLowerCase(),
                );

                if (!indexItem) {
                    console.log('unmapped', columnName);
                    unmappedColumns.push(columnName);
                } else {
                    console.log(`mapped: ${columnName} to ${indexItem.columnName} `);
                    indexItem.mappedField = columnName;
                }
            }
        }

        if (fileError) {
            unmappedColumns = [];
        }

        hasUnmappedRequired = mapSchema.some((s) => s.required && !s.mappedField);

        return { mapSchema, unmappedColumns, tempErrors, sheetColumnNames, fileError, hasUnmappedRequired };
    };

    function containsWhitespace(str: string) {
        return /\s/.test(str);
    }

    const manualMapField = (mappedField: string, schemaColumn: string) => {
        AccountImportStore.update((s, o) => {
            s.unmappedColumns = o.unmappedColumns.filter((s) => s != mappedField);

            const newSchema = structuredClone(o.mapSchema);
            const item = newSchema.find((s) => s.columnName == schemaColumn);

            if (item) {
                item.mappedField = mappedField;
            }

            s.mapSchema = newSchema;
            s.hasUnmappedRequired = newSchema.some((s) => s.required && !s.mappedField);
        });
    };

    const loadAccounts = async () => {
        if (!worksheet) {
            throw 'worksheet not loaded';
        }

        const exceljsonObj = XLSX.utils.sheet_to_json(worksheet) as any[];
        const tempAccounts = [] as Account[];
        const tempErrors = [] as SheetError[];

        for (let rowIndex = 0; rowIndex < exceljsonObj.length; rowIndex++) {
            const data = exceljsonObj[rowIndex];

            const newAccount = {
                id: crypto.randomUUID(),
                name: '',
            } as Account;

            for (let columnIndex = 0; columnIndex < mapSchema.length; columnIndex++) {
                const col = mapSchema[columnIndex];

                if (col.mappedField) {
                    const value = data[col.mappedField];

                    if (col.isSelect && isEmpty(value)) {
                        tempErrors.push({
                            rowNum: rowIndex + 2,
                            colNum: columnIndex + 1,
                            errorType: 'error',
                            message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                        });
                    } else if (value && typeof value === 'string' && value.toString().length > col.maxLength) {
                        tempErrors.push({
                            rowNum: rowIndex + 2,
                            colNum: columnIndex + 1,
                            errorType: 'error',
                            message: `The value in column ${col.mappedField} exceeds the maximum length of ${col.maxLength}. Please fix this in your sheet and upload the file again. `,
                        });
                    } else if (!value) {
                        tempErrors.push({
                            rowNum: rowIndex + 2,
                            colNum: columnIndex + 1,
                            errorType: 'warning',
                            message: `Missing value for column ${col.mappedField}`,
                        });
                    } else {
                        if (col.columnName.toLowerCase() === 'country') {
                            const option = countries.find((p) => p.name.toLowerCase() === value.toLowerCase());

                            if (option) {
                                newAccount.hqCountryId = option.value;
                                newAccount.hqCountryName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadregion') {
                            const option = regions.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newAccount.hqRegionId = option.value;
                                newAccount.hqRegionName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else if (col.columnName.toLowerCase() === 'leadindustry') {
                            const option = industries.find((p) => p.name.toLowerCase() === value.toLowerCase());
                            if (option) {
                                newAccount.industryId = option.value;
                                newAccount.industryName = option.name;
                            } else {
                                tempErrors.push({
                                    rowNum: rowIndex + 2,
                                    colNum: columnIndex + 1,
                                    errorType: 'error',
                                    message: `Invalid value for column ${col.mappedField}. ${value} is not a vaild option.`,
                                });
                            }
                        } else {
                            if (col.columnName === 'accountName') {
                                newAccount['name'] = value;
                            } else {
                                newAccount[col.columnName] = value;
                            }
                        }
                    }
                }
            }

            if (!isEmpty(newAccount.name)) {
                tempAccounts.push(newAccount);
            }
        }

        AccountImportStore.update((s, o) => {
            s.errors = tempErrors;
            s.accounts = [...tempAccounts];
        });
    };

    const setCurrentStep = (value: number) => {
        AccountImportStore.update((s, o) => {
            s.currentStep = value;
        });
    };

    return {
        open,
        onClose,
        onFileUpload,
        openAccountImport,
        errors,
        fileError,
        unmappedColumns,
        loaded,
        mapSchema,
        allColumns,
        hasUnmapped: unmappedColumns.length > 0,
        manualMapField,
        hasUnmappedRequired,
        loadAccounts,
        currentStep,
        setCurrentStep,
        accounts,
        hasWarning: errors.filter((s) => s.errorType === 'warning').length > 0,
        hasError: errors.filter((s) => s.errorType === 'error').length > 0,
    };
};

export default useAccountImport;
